import { RedoOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { getProducts } from 'api';
import { ButtonTextIcon, LineVertical, Pagination } from 'components';
import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useSetTitlePage from 'hook/useSetTitlePage';
import FormDatePicker from 'page/Application/VisitedPage/component/DatePicker';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ButtonOkStyle } from '../ManageCompanyList/component/Form/styled';
import {
  AccountsFileDownloadRanking,
  FilesDownloadRanking,
  HeaderListDataLayout,
  LabelSelectProduct,
  LayoutDefault,
  PartnersFileDownloadRanking,
  TableGroup,
} from './components';
import useMultiLanguageFileDownloadLog from './hook/useMultiLanguageFileDownloadLog';

dayjs.extend(isSameOrAfter);

const FileDownloadLog = () => {
  const { useQueryWithoutParams } = useCustomQuery();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();

  const defaultDateFrom = dayjs().subtract(30, 'day');
  const defaultDateTo = dayjs();

  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
  });
  const [productId, setProductId] = useState(null);
  const [countToReset, setCountToReset] = useState(0);
  const [filterData, setFilterData] = useState({
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    productId: null,
  });
  const [searchText, setSearchText] = useState('');
  const [isAccountsTableLoading, setIsAccountsTableLoading] = useState(false);
  const [isFilesDownloadTableLoading, setIsFilesDownloadTableLoading] =
    useState(false);
  const [isPartnersTableLoading, setIsPartnersTableLoading] = useState(false);
  const [emptyText, setEmptyText] = useState('データがありません');

  const { FILE_DOWNLOAD_LOG_LIST_TITLE } = useMultiLanguageFileDownloadLog();

  const { data: productsList = [] } = useQueryWithoutParams({
    api: getProducts,
    key: 'getProducts',
  });

  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/'}>企業管理</Link>,
      },
      {
        title: FILE_DOWNLOAD_LOG_LIST_TITLE,
      },
    ];
  }, []);

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPageSize(value);
    } else {
      errorNetWork();
    }
  };

  const handleChangeDateRange = (values) => {
    try {
      if (isOnline) {
        if (values && values.length === 2) {
          const dateFrom = values[0] ? values[0] : '';
          const dateTo = values[1] ? values[1] : '';
          if (dayjs(dateFrom).isAfter(dayjs(dateTo))) {
            setDateRange({ dateFrom: dateTo, dateTo: dateFrom });
          } else {
            setDateRange({ dateFrom, dateTo });
          }
        } else {
          setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
        }
      } else {
        errorNetWork();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFilterData = () => {
    try {
      if (isOnline) {
        setFilterData({
          ...dateRange,
          productId,
        });
        setCountToReset((prev) => prev + 1);
        setEmptyText('データが見つかりませんでした。');
      } else {
        errorNetWork();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setDateRange({ dateFrom: defaultDateFrom, dateTo: defaultDateTo });
      setProductId(null);
      setPageSize(10);
      setCountToReset((prev) => prev + 1);
      setFilterData({
        dateFrom: defaultDateFrom,
        dateTo: defaultDateTo,
        productId: null,
      });
      setEmptyText('データがありません');
    } else {
      errorNetWork();
    }
  };

  const handleSelectProduct = (value) => {
    setProductId(value);
  };

  const handleSearch = (value) => {
    if (value && value.length > 256) {
      setSearchText(value.slice(0, 256));
      return value.slice(0, 256);
    }
    setSearchText(value);
    return value;
  };

  useSetTitlePage(
    `${FILE_DOWNLOAD_LOG_LIST_TITLE} | Soliton パートナーサイト`,
    true,
  );

  return (
    <>
      {contextErrorNetWork}
      <Breadcrumb
        listItem={listItem}
        titlePage={FILE_DOWNLOAD_LOG_LIST_TITLE}
      />
      <LayoutDefault>
        <HeaderListDataLayout
          value={pageSize}
          handleChangePageSize={handleChangePageSize}
          pageSizeOptions={defaultSelectPageSizeOption}
          titleScreen={FILE_DOWNLOAD_LOG_LIST_TITLE}
          disabled={
            isAccountsTableLoading ||
            isFilesDownloadTableLoading ||
            isPartnersTableLoading
          }
        >
          <>
            <ButtonTextIcon
              text={'リフレッシュ'}
              icon={<RedoOutlined />}
              onClick={handleResetData}
              disabled={
                isAccountsTableLoading ||
                isFilesDownloadTableLoading ||
                isPartnersTableLoading
              }
            />
            <LineVertical />
            <LabelSelectProduct>プロダクト名</LabelSelectProduct>
            <Select
              showSearch
              placeholder="プロダクト名を入力してください。"
              optionFilterProp="label"
              onChange={handleSelectProduct}
              onSearch={handleSearch}
              searchValue={searchText}
              options={
                productsList?.data?.map((item) => ({
                  value: item.productId,
                  label: item.productName,
                })) || []
              }
              style={{ width: 300, height: 36 }}
              value={productId}
              filterOption={(input, option) => {
                return (
                  option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              disabled={
                isAccountsTableLoading ||
                isFilesDownloadTableLoading ||
                isPartnersTableLoading
              }
            />
            <LineVertical />
            <FormDatePicker
              width={'360px'}
              height={'36px'}
              dateFormat={DATE_FORMAT_LIST[1]}
              isShowTime={false}
              required={false}
              defaultFromDate={dateRange.dateFrom}
              defaultToDate={dateRange.dateTo}
              resetTrigger={countToReset}
              setDateRange={setDateRange}
              listDisable={
                isAccountsTableLoading ||
                isFilesDownloadTableLoading ||
                isPartnersTableLoading
              }
            />
            <LineVertical />
            <ButtonOkStyle
              type="primary"
              htmlType="submit"
              onClick={handleFilterData}
              disabled={
                isAccountsTableLoading ||
                isFilesDownloadTableLoading ||
                isPartnersTableLoading
              }
            >
              {' '}
              検索
            </ButtonOkStyle>
          </>
        </HeaderListDataLayout>
        <TableGroup>
          <PartnersFileDownloadRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            setIsPartnersTableLoading={setIsPartnersTableLoading}
            emptyText={emptyText}
          />
          <AccountsFileDownloadRanking
            filterData={filterData}
            pageSize={pageSize}
            countToReset={countToReset}
            setIsAccountsTableLoading={setIsAccountsTableLoading}
            emptyText={emptyText}
          />
        </TableGroup>
        <FilesDownloadRanking
          filterData={filterData}
          pageSize={pageSize}
          countToReset={countToReset}
          setIsFilesDownloadTableLoading={setIsFilesDownloadTableLoading}
          emptyText={emptyText}
        />
        <Pagination
          handleChangePageSize={handleChangePageSize}
          pageSize={pageSize}
          pageSizeOptions={defaultPageSizeOptions}
          isShowTotal={false}
          isShowPagination={false}
        />
      </LayoutDefault>
    </>
  );
};
export default FileDownloadLog;
